// dropPrimaryKey — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-primary-key' })` op (DSE2 §3.B1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
//
// Per-engine rules (mirrors DSE2's `synthesizeDropPrimaryKey`):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mysql: emits `DROP PRIMARY KEY` — `constraintName` is accepted for API
//     symmetry but ignored. The form hides the name input.
//   - mssql / pgsql / oracle: `constraintName` is required (drop by name).
//   - pgsql / oracle: `cascade?: boolean` appended as `CASCADE`.

import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type DropPrimaryKeyArgs = {
  readonly constraintName?: string;
  readonly cascade?: boolean;
};

export function buildDropPrimaryKeyEdit(args: DropPrimaryKeyArgs): DdlEdit {
  return {
    op: 'drop-primary-key',
    constraintName: args.constraintName,
    cascade: args.cascade,
  };
}

export async function commitDropPrimaryKey(
  ctx: VqeDdlEditContext,
  args: DropPrimaryKeyArgs,
): Promise<void> {
  const edit = buildDropPrimaryKeyEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isDropPrimaryKeySupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getDropPrimaryKeyUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … DROP PRIMARY KEY. Recreate '
      + 'the table without the constraint — use “Edit as SQL” to write '
      + 'the change.'
    );
  }
  return '';
}

/** mssql / pgsql / oracle all require the constraint name on DROP because they
 *  drop PRIMARY KEY constraints by name (`DROP CONSTRAINT <name>`). mysql does
 *  NOT — its primary key is always named `PRIMARY` internally and the
 *  synthesizer emits `DROP PRIMARY KEY` (no name). */
export function requiresConstraintName(engine: SqlEngine): boolean {
  return engine === 'mssql' || engine === 'postgres' || engine === 'oracle';
}

export function isCascadeSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

/** Pre-flight validator: when the engine requires a constraint name and none
 *  is supplied, the synthesizer would throw. Surface the error in the form so
 *  the user sees it before submit. */
export function validateDropPrimaryKeyForEngine(
  engine: SqlEngine,
  args: DropPrimaryKeyArgs,
): string | null {
  if (engine === 'sqlite') {
    return getDropPrimaryKeyUnsupportedTooltip(engine);
  }
  if (requiresConstraintName(engine)) {
    const name = args.constraintName?.trim() ?? '';
    if (name === '') {
      return `${engine} requires the constraint name to drop a primary key.`;
    }
  }
  if (!isCascadeSupported(engine) && args.cascade === true) {
    return 'CASCADE is supported on PostgreSQL + Oracle only.';
  }
  return null;
}
